import React, { useMemo, useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { LuInfo, LuX } from "react-icons/lu";
import {
  bodyCellSx,
  cardSx,
  EmptyTableRow,
  headCellSx,
  primaryButtonSx,
} from "../AssetListing/assetListingShared";
import {
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
  OFF_WHITE,
  PROFILE_FONT,
} from "../../constants/layout";

const ICON_BLUE = INPUT_BORDER_FOCUS;
const BORDER = "#E8E8E8";
const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";

interface RMTableProps {
  users: any[];
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  isAdmin?: boolean;
  onEdit?: (row: any) => void;
  searchQuery?: string;
}

const getHoldingTypeLabel = (type: number) => {
  switch (type) {
    case 2:
      return "Mutual Fund";
    case 3:
      return "Fixed Deposit";
    case 1:
      return "Bond";
    case 4:
      return "Equity";
    case 5:
      return "AIF";
    case 6:
      return "PMS";
    case 7:
      return "PPF";
    case 8:
      return "Unlisted Equity";
    default:
      return "NPS";
  }
};

const formatCurrency = (value: number | string | null | undefined) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return `₹ ${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const toDisplayText = (value: unknown) => {
  if (value == null) return "";
  return String(value).trim();
};

const getSchemeDisplay = (user: any) => toDisplayText(user?.schemeName) || "—";

const getIsinDisplay = (user: any) => toDisplayText(user?.isin) || "—";

const formatNumberDisplay = (value: number | string | null | undefined) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-IN", { maximumFractionDigits: 4 });
};

const getCommentDisplay = (user: any) => toDisplayText(user?.adminComment) || "—";

const getInvestorName = (user: any) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();

const hasDetails = (user: any) =>
  Boolean(toDisplayText(user?.schemeName) || toDisplayText(user?.adminComment));

const twoLineHeadCellSx = {
  ...headCellSx,
  whiteSpace: "normal" as const,
  lineHeight: 1.35,
  verticalAlign: "bottom" as const,
};

const TwoLineTableHead = ({
  line1,
  line2,
  align = "right",
}: {
  line1: string;
  line2: string;
  align?: "right" | "center" | "left";
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start",
    }}
  >
    <span>{line1}</span>
    <span>{line2}</span>
  </Box>
);

const PartnerFinancePage: React.FC<RMTableProps> = ({
  users,
  isAdmin = false,
  onEdit,
  searchQuery = "",
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [detailRow, setDetailRow] = useState<any | null>(null);

  const columnCount = 12;

  const filteredRows = useMemo(() => {
    if (!Array.isArray(users)) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return users;

    return users.filter((user) => {
      const investor = getInvestorName(user).toLowerCase();
      const product = getHoldingTypeLabel(Number(user?.holdingType)).toLowerCase();
      const scheme = String(user?.schemeName || "").toLowerCase();
      const isin = String(user?.isin || "").toLowerCase();
      const comment = String(user?.adminComment || "").toLowerCase();
      return (
        investor.includes(q) ||
        product.includes(q) ||
        scheme.includes(q) ||
        isin.includes(q) ||
        comment.includes(q)
      );
    });
  }, [users, searchQuery]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  return (
    <>
      <Box sx={{ ...cardSx, overflow: "hidden" }}>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ tableLayout: "fixed", minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, width: "8%" }}>Date</TableCell>
                <TableCell sx={{ ...headCellSx, width: "12%" }} align="center">
                  Investor
                </TableCell>
                <TableCell sx={{ ...headCellSx, width: "9%" }} align="center">
                  Product Type
                </TableCell>
                <TableCell sx={{ ...headCellSx, width: "14%" }} align="left">
                  Scheme
                </TableCell>
                <TableCell sx={{ ...headCellSx, width: "10%" }} align="left">
                  ISIN
                </TableCell>
                <TableCell sx={{ ...headCellSx, width: "7%" }} align="right">
                  Units
                </TableCell>
                <TableCell sx={{ ...headCellSx, width: "7%" }} align="right">
                  NAV
                </TableCell>
                <TableCell sx={{ ...headCellSx, width: "9%" }} align="right">
                  Brokerage (₹)
                </TableCell>
                <TableCell sx={{ ...twoLineHeadCellSx, width: "8%" }} align="right">
                  <TwoLineTableHead line1="Partner Share" line2="(%)" />
                </TableCell>
                <TableCell sx={{ ...twoLineHeadCellSx, width: "10%" }} align="right">
                  <TwoLineTableHead line1="AumSampann Earnings" line2="(₹)" />
                </TableCell>
                <TableCell sx={{ ...twoLineHeadCellSx, width: "10%" }} align="right">
                  <TwoLineTableHead line1="Partner Earnings" line2="(₹)" />
                </TableCell>
                <TableCell sx={{ ...headCellSx, width: isAdmin ? "8%" : "6%" }} align="center">
                  Details
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.length === 0 ? (
                <TableRow>
                  <EmptyTableRow
                    colSpan={columnCount}
                    message="Payout details not available for this partner"
                  />
                </TableRow>
              ) : (
                paginatedRows.map((user, index) => {
                  const investorName = getInvestorName(user);

                  return (
                    <TableRow key={user?.id ?? index} hover sx={{ "&:hover": { bgcolor: "#FAFAFA" } }}>
                      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }}>
                        {user?.paymentDate || "—"}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY, textAlign: "center" }}>
                        <Typography
                          sx={{
                            fontFamily: PROFILE_FONT,
                            fontSize: 14,
                            color: investorName ? DARK_NAVY : DARK_NAVY_LIGHT,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {investorName || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT, textAlign: "center" }}>
                        <Typography
                          sx={{
                            fontFamily: PROFILE_FONT,
                            fontSize: 14,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {getHoldingTypeLabel(Number(user?.holdingType))}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY }} align="left">
                        <Typography
                          sx={{
                            fontFamily: PROFILE_FONT,
                            fontSize: 13,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={getSchemeDisplay(user)}
                        >
                          {getSchemeDisplay(user)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }} align="left">
                        <Typography
                          sx={{
                            fontFamily: PROFILE_FONT,
                            fontSize: 13,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={getIsinDisplay(user)}
                        >
                          {getIsinDisplay(user)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }} align="right">
                        {formatNumberDisplay(user?.units)}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }} align="right">
                        {formatNumberDisplay(user?.nav)}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY }} align="right">
                        {formatCurrency(user?.brockerage)}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }} align="right">
                        {Number(user?.partnerPercentage) * 100}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY }} align="right">
                        {formatCurrency(user?.amountToAumSampann)}
                      </TableCell>
                      <TableCell
                        sx={{ ...bodyCellSx, color: "#08991F", fontWeight: 600 }}
                        align="right"
                      >
                        {formatCurrency(user?.amountToPartner)}
                      </TableCell>
                      <TableCell sx={bodyCellSx} align="center">
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            aria-label="View scheme and comment"
                            onClick={() => setDetailRow(user)}
                            disabled={!hasDetails(user)}
                            sx={{
                              color: hasDetails(user) ? ICON_BLUE : DARK_NAVY_LIGHT,
                              borderRadius: "8px",
                              opacity: hasDetails(user) ? 1 : 0.4,
                              "&:hover": { bgcolor: OFF_WHITE, color: ICON_BLUE },
                            }}
                          >
                            <LuInfo size={18} />
                          </IconButton>
                          {isAdmin ? (
                            <IconButton
                              size="small"
                              aria-label="edit payout"
                              onClick={() => onEdit?.(user)}
                              disabled={!user?.id}
                              sx={{
                                color: DARK_NAVY_LIGHT,
                                borderRadius: "8px",
                                "&:hover": { bgcolor: OFF_WHITE, color: ICON_BLUE },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ borderTop: `1px solid ${BORDER}` }}>
          <TablePagination
            component="div"
            rowsPerPageOptions={[25, 50, 100]}
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelDisplayedRows={({ from, to, count }) =>
              `Showing ${from} to ${to} of ${count} results`
            }
            sx={{
              fontFamily: PROFILE_FONT,
              color: DARK_NAVY,
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                fontFamily: PROFILE_FONT,
                fontSize: 13,
              },
            }}
          />
        </Box>
      </Box>

      <Dialog
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            fontFamily: PROFILE_FONT,
            boxShadow: CARD_SHADOW,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: PROFILE_FONT,
            fontWeight: 700,
            fontSize: 18,
            color: DARK_NAVY,
            pr: 6,
            pb: 1,
          }}
        >
          Transaction Details
          <IconButton
            onClick={() => setDetailRow(null)}
            aria-label="Close"
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: DARK_NAVY_LIGHT,
              "&:hover": { bgcolor: OFF_WHITE, color: ICON_BLUE },
            }}
          >
            <LuX size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography
                sx={{
                  fontFamily: PROFILE_FONT,
                  fontSize: 12,
                  fontWeight: 600,
                  color: DARK_NAVY_LIGHT,
                  mb: 0.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Scheme Name
              </Typography>
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY, lineHeight: 1.6 }}>
                {detailRow ? getSchemeDisplay(detailRow) : "—"}
              </Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: PROFILE_FONT,
                  fontSize: 12,
                  fontWeight: 600,
                  color: DARK_NAVY_LIGHT,
                  mb: 0.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Comment
              </Typography>
              <Typography
                sx={{
                  fontFamily: PROFILE_FONT,
                  fontSize: 14,
                  color: DARK_NAVY,
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {detailRow ? getCommentDisplay(detailRow) : "—"}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDetailRow(null)} sx={primaryButtonSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PartnerFinancePage;
